import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileSignature, Plus, Loader2, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
export const money = (n) => (n == null ? "—" : n.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }));
export const inputCls = "w-full bg-navy-900 border border-gold-500/20 text-white text-sm px-3 py-2 focus:border-gold-500/60 outline-none";
export const DL_CLR = { overdue: "text-red-400 border-red-500/40", due_soon: "text-gold-400 border-gold-500/40", upcoming: "text-white/50 border-gold-500/20" };

export function Field({ label, children }) {
  return (
    <label className="block">
      <Mono className="text-[0.5rem] text-white/40 block mb-1">{label}</Mono>
      {children}
    </label>
  );
}

const PHASE = { construction: "text-gold-400 border-gold-500/40", dlp: "text-sky-300 border-sky-400/40", final: "text-emerald-400 border-emerald-400/40" };

function NewContractModal({ onClose, onCreated }) {
  const [f, setF] = useState({ title: "", contractor: "", principal: "", superintendent: "", form: "AS2124", state: "nsw", contract_sum: "", commencement: "", pc_date: "", dlp_months: 12, retention_pct: 10, retention_cap_pct: 5, job_number: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    if (!f.title) return toast.error("Contract title is required");
    setBusy(true);
    try {
      const { data } = await api.post("/ca/contracts", { ...f, contract_sum: +f.contract_sum || 0, dlp_months: +f.dlp_months, retention_pct: +f.retention_pct, retention_cap_pct: +f.retention_cap_pct, commencement: f.commencement || null, pc_date: f.pc_date || null });
      toast.success("Contract created");
      onCreated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ca-new-modal">
      <div className="absolute inset-0 bg-navy-950/85" onClick={onClose} />
      <div className="relative bg-navy-900 border border-gold-500/30 w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <Mono className="text-gold-400 text-[0.55rem]">New contract</Mono>
          <button onClick={onClose} data-testid="ca-new-close"><X className="h-4 w-4 text-white/50" /></button>
        </div>
        <Field label="Contract title / package"><input value={f.title} onChange={set("title")} placeholder="e.g. RRIF Package 2 — Bee Creek Rd reconstruction" className={inputCls} data-testid="ca-title" /></Field>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Contractor"><input value={f.contractor} onChange={set("contractor")} className={inputCls} data-testid="ca-contractor" /></Field>
          <Field label="Principal"><input value={f.principal} onChange={set("principal")} className={inputCls} data-testid="ca-principal" /></Field>
          <Field label="Superintendent"><input value={f.superintendent} onChange={set("superintendent")} className={inputCls} data-testid="ca-superintendent" /></Field>
          <Field label="Contract form">
            <select value={f.form} onChange={set("form")} className={inputCls} data-testid="ca-form">
              {["AS2124", "AS4000", "AS4905", "AS4906"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="State (SOPA regime)">
            <select value={f.state} onChange={set("state")} className={inputCls} data-testid="ca-state">
              <option value="nsw">NSW</option><option value="qld">QLD</option>
            </select>
          </Field>
          <Field label="Contract sum ($ ex GST)"><input type="number" value={f.contract_sum} onChange={set("contract_sum")} className={inputCls} data-testid="ca-sum" /></Field>
          <Field label="Commencement"><input type="date" value={f.commencement} onChange={set("commencement")} className={inputCls} data-testid="ca-commencement" /></Field>
          <Field label="Date for PC"><input type="date" value={f.pc_date} onChange={set("pc_date")} className={inputCls} data-testid="ca-pc-date" /></Field>
          <Field label="DLP (months)"><input type="number" value={f.dlp_months} onChange={set("dlp_months")} className={inputCls} data-testid="ca-dlp" /></Field>
          <Field label="Retention %"><input type="number" value={f.retention_pct} onChange={set("retention_pct")} className={inputCls} data-testid="ca-retention" /></Field>
          <Field label="Retention cap % of sum"><input type="number" value={f.retention_cap_pct} onChange={set("retention_cap_pct")} className={inputCls} data-testid="ca-retention-cap" /></Field>
          <Field label="Authority job number"><input value={f.job_number} onChange={set("job_number")} placeholder="e.g. JC-4471" className={inputCls} data-testid="ca-job-number" /></Field>
        </div>
        <button onClick={save} disabled={busy} data-testid="ca-save-contract" className="w-full bg-gold-500 text-navy-950 font-semibold text-sm py-2.5 hover:bg-gold-400 disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create contract
        </button>
      </div>
    </div>
  );
}

export default function ContractAdmin() {
  const [contracts, setContracts] = useState(null);
  const [deadlines, setDeadlines] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/ca/contracts"), api.get("/ca/deadlines")])
      .then(([c, d]) => { setContracts(c.data); setDeadlines(d.data); })
      .catch(() => setContracts([]));
  }, []);

  if (!contracts) return <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold-400" /></div>;

  return (
    <div className="space-y-6" data-testid="ca-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Mono className="text-gold-400 text-[0.55rem] flex items-center gap-2"><FileSignature className="h-3.5 w-3.5" /> Contract Admin · AS 2124 / AS 4000</Mono>
          <h1 className="font-heading text-3xl text-white mt-2">Contract administration</h1>
          <p className="text-white/50 text-sm mt-2 max-w-3xl">Progress claims, certificates, variations and EOTs with the time-bars computed for you — AS form rules plus the NSW SOP Act and QLD BIF Act payment-schedule clocks.</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="ca-new-btn" className="bg-gold-500 text-navy-950 font-semibold text-sm px-5 py-2.5 hover:bg-gold-400 flex items-center gap-2 mt-2">
          <Plus className="h-4 w-4" /> New contract
        </button>
      </div>

      {deadlines?.deadlines?.length > 0 && (
        <div className="border border-gold-500/20 bg-navy-900/40 p-5" data-testid="ca-deadlines-strip">
          <Mono className="text-gold-400 text-[0.55rem] block mb-3">Time-bars & deadlines · {deadlines.counts.overdue} overdue · {deadlines.counts.due_soon} due soon</Mono>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {deadlines.deadlines.slice(0, 9).map((d, i) => (
              <button key={i} onClick={() => navigate(`/dashboard/contracts/${d.contract_id}`)} data-testid={`ca-deadline-${i}`}
                className={`text-left border px-3 py-2 hover:bg-navy-900/70 transition-colors ${DL_CLR[d.status]}`}>
                <div className="text-xs flex items-center gap-1.5">{d.status === "overdue" && <AlertTriangle className="h-3 w-3" />}{d.title}</div>
                <div className="text-[0.6rem] text-white/40 mt-0.5">{d.contract} · due {d.due || "—"}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="border border-gold-500/20 bg-navy-900/40" data-testid="ca-register">
        <div className="px-5 py-3 border-b border-gold-500/15"><Mono className="text-gold-400 text-[0.55rem]">Contracts register · {contracts.length}</Mono></div>
        {contracts.length === 0 ? (
          <div className="p-10 text-center text-white/40 text-sm" data-testid="ca-empty">No contracts yet — create the first works package above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b border-gold-500/15">
                {["Contract", "Contractor", "Form / State", "Adjusted sum", "Certified", "Retention", "Phase"].map((h) => (
                  <th key={h} className="px-4 py-2"><Mono className="text-[0.5rem] text-white/40">{h}</Mono></th>
                ))}
              </tr></thead>
              <tbody>
                {contracts.map((c) => (
                  <tr key={c.id} className="border-b border-gold-500/10 hover:bg-navy-900/60 cursor-pointer" data-testid={`ca-row-${c.id}`} onClick={() => navigate(`/dashboard/contracts/${c.id}`)}>
                    <td className="px-4 py-3">
                      <Link to={`/dashboard/contracts/${c.id}`} className="text-white text-xs font-medium hover:text-gold-400" data-testid={`ca-link-${c.id}`}>{c.title}</Link>
                      <div className="text-white/40 text-[0.65rem] mt-0.5">{c.superintendent ? `Supt: ${c.superintendent}` : c.principal}</div>
                    </td>
                    <td className="px-4 py-3 text-white/70 text-xs">{c.contractor || "—"}</td>
                    <td className="px-4 py-3"><Mono className="text-[0.5rem] text-gold-400 border border-gold-500/30 px-1.5 py-0.5">{c.form}</Mono> <Mono className="text-[0.5rem] text-sky-300 border border-sky-400/30 px-1.5 py-0.5 ml-1">{(c.state || "").toUpperCase()}</Mono></td>
                    <td className="px-4 py-3 text-white/80 text-xs font-mono">{money(c.summary?.adjusted_sum)}</td>
                    <td className="px-4 py-3 text-white/70 text-xs font-mono">{money(c.summary?.certified_total)}</td>
                    <td className="px-4 py-3 text-white/70 text-xs font-mono">{money(c.summary?.retention_held)}</td>
                    <td className="px-4 py-3"><Mono className={`text-[0.5rem] border px-1.5 py-0.5 ${PHASE[c.summary?.phase] || ""}`}>{c.summary?.phase}</Mono></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNew && <NewContractModal onClose={() => setShowNew(false)} onCreated={(c) => { setContracts((x) => [c, ...x]); setShowNew(false); }} />}
    </div>
  );
}
